import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(formData.email, formData.password, rememberMe);
    
    if (result.success) {
      navigate('/admin/dashboard');
    } else {
      toast.error(result.error || 'Autentificare eșuată');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Lock className="w-16 h-16 text-[#7BA05B] mx-auto mb-4" />
          <h1 className="text-3xl font-medium text-[#2A2724]" style={{ fontFamily: 'Outfit' }}>
            Admin Login
          </h1>
          <p className="text-[#7A7369] mt-2">Acces panel de administrare</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 border border-[#E8E2D9] space-y-6">
          <div>
            <Label htmlFor="email" className="text-[#2A2724] mb-2 block">Email</Label>
            <Input
              data-testid="admin-email-input"
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="admin@cantina.ro"
              className="rounded-xl border-[#E8E2D9] focus:border-[#7BA05B]"
              required
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-[#2A2724] mb-2 block">Parolă</Label>
            <Input
              data-testid="admin-password-input"
              id="password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              className="rounded-xl border-[#E8E2D9] focus:border-[#7BA05B]"
              required
            />
          </div>

          {/* Remember Me */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="checkbox"
              aria-checked={rememberMe}
              data-testid="remember-me-checkbox"
              onClick={() => setRememberMe(!rememberMe)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                rememberMe
                  ? 'bg-[#7BA05B] border-[#7BA05B]'
                  : 'bg-white border-[#C8C0B8] hover:border-[#7BA05B]'
              }`}
            >
              {rememberMe && (
                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2 6l3 3 5-5" />
                </svg>
              )}
            </button>
            <label
              htmlFor="remember-me"
              className="text-sm text-[#5A5450] cursor-pointer select-none"
              onClick={() => setRememberMe(!rememberMe)}
              data-testid="remember-me-label"
            >
              Ține-mă conectat{' '}
              <span className="text-[#9A928A]">(30 zile)</span>
            </label>
          </div>

          <Button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-[#7BA05B] hover:bg-[#5C8945] text-white h-12 font-medium"
          >
            {loading ? 'Autentificare...' : 'Autentifică-te'}
          </Button>
        </form>

        <div className="text-center mt-6">
          <Button
            data-testid="back-to-client-button"
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-[#7A7369] hover:text-[#2A2724]"
          >
            Înapoi la site-ul client
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;